<template
  xmlns="http://www.w3.org/1999/html"
>
  <Wrapper :team="team">
    <div>
      <div class="mt-10 divide-y divide-gray-200">
        <div class="flex flex-row justify-between">
          <div class="space-y-1">
            <h3 class="text-lg leading-6 font-medium text-gray-900">
              Team members
            </h3>
            <p class="max-w-2xl text-sm text-gray-500">
              Here you'll find all members in your organisation and their role/permissions.
            </p>
          </div>
          <button
            v-if="can('invite members')"
            type="button"
            class=" mt-5 cursor-pointer inline-flex justify-center px-4 py-3 border border-primary shadow-sm text-sm font-medium rounded-md text-font-darkest bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            @click="showAddTeamMemberModal = true"
          >
            <!-- Heroicon name: user-add -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 mr-2 text-gray-400 sm:hidden lg:block"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
            </svg>
            Add Team Member
          </button>
        </div>
        <div class="mt-6">
          <!-- Company members section -->
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Name
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Role
                </th>
                <th
                  scope="col"
                  class="relative px-6 py-3"
                >
                  <span class="sr-only">Edit</span>
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-if="team.owner">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="flex-shrink-0 h-10 w-10">
                      <img
                        class="h-10 w-10 rounded-full object-cover"
                        :src="team.owner.profile_photo_url"
                        alt=""
                      >
                    </div>
                    <div class="ml-4">
                      <div class="text-sm font-medium text-gray-900">
                        {{ team.owner.name }}
                      </div>
                      <div class="text-sm text-gray-500">
                        {{ team.owner.email }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    Active
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                  {{ team.owner.role.name }}
                </td>
              </tr>
              <tr
                v-for="(member, idx) in team.users"
                :key="idx"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="flex-shrink-0 h-10 w-10">
                      <img
                        class="h-10 w-10 rounded-full object-cover"
                        :src="member.profile_photo_url"
                        alt=""
                      >
                    </div>
                    <div class="ml-4">
                      <div class="text-sm font-medium text-gray-900">
                        {{ member.name }}
                      </div>
                      <div class="text-sm text-gray-500">
                        {{ member.email }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 capitalize"
                    :class="{'bg-font-lightest text-font-dark': member.membership.status == 'inactive'}"
                  >
                    {{ member.membership.status ? member.membership.status : 'None' }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                  {{ member.role ? member.role.name : 'None' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <a
                    class="cursor-pointer text-indigo-600 hover:text-indigo-900"
                    @click="editTeamMember(member)"
                  >Edit</a>
                </td>
              </tr>
            </tbody>
          </table>
          <!-- Company members section end -->
        </div>
      </div>
      <div class="mt-10 divide-y divide-gray-200">
        <div class="flex flex-row justify-between">
          <div class="space-y-1">
            <h3 class="text-lg leading-6 font-medium text-gray-900">
              Team invitations
            </h3>
            <p class="max-w-2xl text-sm text-gray-500">
              Here you'll find all invitations to join your Team.
            </p>
          </div>
        </div>
        <div class="mt-6">
          <!-- Team Invitation section -->
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Name
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Role
                </th>
                <th
                  scope="col"
                  class="relative px-6 py-3"
                >
                  <span class="sr-only">Edit</span>
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="(invitation, idx) in team.team_invitations"
                :key="idx"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="ml-4">
                      <div class="text-sm text-gray-500">
                        {{ invitation.email }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-200 text-gray-900">
                    Invited
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                  {{ invitation.role }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <!-- Cancel Team Invitation -->
                  <button
                    v-if="can('remove members')"
                    class="cursor-pointer ml-6 text-sm text-red-500 focus:outline-none"
                    @click="cancelTeamInvitation(invitation)"
                  >
                    Cancel
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal for Add new Team Member  -->
    <add-team-member
      :team="team"
      :available-statuses="availableStatuses"
      :available-roles="availableRoles"
      :show="showAddTeamMemberModal"
      @close="showAddTeamMemberModal = false"
    />

    <!-- Modal for Edit Team Member  -->
    <edit-team-member
      :team="team"
      :available-statuses="availableStatuses"
      :available-roles="availableRoles"
      :member="member"
      @close="member = null"
    />
  </Wrapper>
</template>

<script>
import Wrapper from './Wrapper.vue';
import AddTeamMember from '../../../ComponentsDeprecated/Modal/AddTeamMember.vue';
import EditTeamMember from '../../../ComponentsDeprecated/Modal/EditTeamMember.vue';

export default {
  components: {
    Wrapper,
    AddTeamMember,
    EditTeamMember,
  },

  props: [
    'team',
    'availableRoles',
    'availableStatuses',
    'defaultPermissions',
  ],
  data() {
    return {
      showEditMemberModal: false,
      showAddTeamMemberModal: false,
      member: null,
    };
  },
  methods: {
    cancelTeamInvitation(invitation) {
      this.$inertia.delete(route('team-invitations.destroy', invitation), {
        onSuccess: () => {
          this.$notify({
            type: 'info',
            title: 'Invitation cancelled',
            text: `The invitation to ${invitation.email} was cancelled`,
          }, 4000);
        },
        onError: () => {
          this.$notify({
            type: 'danger',
            title: 'Something went wrong',
            text: 'Could not cancel the invitation',
          }, 4000);
        },
      });
    },
    editTeamMember(member) {
      this.member = member;
    },
  },
};
</script>
